import { useEffect, useState } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

import {
	EMPTY_PAGE_CONTENT_RECT,
	getPageContentRect,
	isSamePageContentRect,
	subscribeToPageContentRect,
	type PageContentRect,
} from '../utils/pageContentDimensionsStore.ts';

export type PageContentDimensions = {
	width: number | string;
	left?: number;
	right?: number;
};

/**
 * Custom hook to track page-content-only dimensions for layout alignment and blue background sizing
 * @returns Object containing width, left, and right dimensions of the page-content-only element
 */
export const usePageContentDimensions = (): PageContentDimensions => {
	const [dimensions, setDimensions] = useState<PageContentRect>(EMPTY_PAGE_CONTENT_RECT);

	useEffect(() => {
		const publish = (rect: PageContentRect) =>
			setDimensions((current) => (isSamePageContentRect(current, rect) ? current : rect));

		// `ViewPageContent` registers the element with the store through a callback ref, so there is
		// nothing to look up in the DOM and no MutationObserver to keep armed. Every consumer of
		// this hook shares one ResizeObserver instead of creating its own pair of observers.
		//
		// This is the experiment's single exposure point: it fires when a consumer actually starts
		// tracking dimensions, which is exactly the population whose cohorts behave differently.
		// `ViewPageContent` deliberately uses the no-exposure variant so that rendering the page
		// content div does not log an exposure on its own.
		if (expValEquals('confluence_frontend_page_content_dimensions_store', 'isEnabled', true)) {
			publish(getPageContentRect());
			return subscribeToPageContentRect(publish);
		}

		// Everything below is the control behaviour: find the element by querying the DOM, and keep
		// a MutationObserver armed on `document.body` in case the element is rendered later or
		// replaced. That observer runs a full-document attribute-selector query on every DOM
		// mutation anywhere on the page and never disconnects — including on routes where the
		// element never appears at all, such as the full page editor. Delete with the experiment.
		let resizeObserver: ResizeObserver | null = null;
		let pageContentOnly: Element | null = null;

		const updateDimensions = () => {
			if (!pageContentOnly) {
				return;
			}
			const { width, left, right } = pageContentOnly.getBoundingClientRect();
			publish({ width, left, right });
		};

		// Set up window resize listener once (outside setupObservers to avoid duplicates)
		window.addEventListener('resize', updateDimensions);

		const setupObservers = (element: Element) => {
			pageContentOnly = element;

			// Clean up existing ResizeObserver if any
			if (resizeObserver) {
				resizeObserver.disconnect();
			}

			resizeObserver = new ResizeObserver(updateDimensions);
			resizeObserver.observe(element);

			// Initial dimension calculation
			updateDimensions();
		};

		// Check if element already exists
		const existingElement = document.querySelector("[data-testid='page-content-only']");
		if (existingElement) {
			setupObservers(existingElement);
		}

		// Set up MutationObserver to watch for when element is rendered or replaced
		const mutationObserver = new MutationObserver(() => {
			const newPageContentOnly = document.querySelector("[data-testid='page-content-only']");

			// If element exists and is different from current one, set up observers
			if (newPageContentOnly && newPageContentOnly !== pageContentOnly) {
				setupObservers(newPageContentOnly);
			}
		});

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			window.removeEventListener('resize', updateDimensions);
			mutationObserver.disconnect();
		};
	}, []);

	return dimensions;
};
